import React from 'react';
import { Card } from '@mts/ui';
import { Pencil } from 'lucide-react';
import { SOCIAL_PLATFORMS } from '@mts/shared/socialLinks';
import { publicSocialProfileLinks } from '../../../utils/listingSchemaFacts.js';
import SocialIconButton from '../../../components/common/SocialIconButton';

// Google Business Profile remains a display-only legacy link.
const ALL_PLATFORMS = [...SOCIAL_PLATFORMS.map(({ platform }) => platform), 'google'];


function BasicSocialLinksCard({ socialLinks, canEdit = false, onEdit }) {
  // Normalize socialLinks to a platform → url map for quick lookup
  const linkMap = React.useMemo(() => publicSocialProfileLinks(socialLinks), [socialLinks]);

  if (!canEdit && !ALL_PLATFORMS.some(platform => linkMap[platform])) return null;

  return (
    <div id="SocialCard">
      <Card
        id="SocialCard-Card"
        className="my-[10px] rounded-lg"
        style={{
          backgroundColor: 'var(--card)',
          borderColor: 'var(--border)',
          borderWidth: '1px',
          borderStyle: 'solid',
        }}
      >
      <Card.Header id="SocialCard-CardHeader" className="flex items-start justify-between gap-3">
        <div id="SocialCard-CardHeaderInner">
          <h2 className="text-base font-semibold text-[var(--primary)]">Social</h2>
          <Card.Title id="SocialCard-CardTitle">
            <span className="sr-only">Social profiles</span>
          </Card.Title>
          <Card.Description id="SocialCard-CardDescription">
            <span className="sr-only">Business social media links</span>
          </Card.Description>
        </div>
        <Card.Action id="SocialCard-CardAction">
          {canEdit ? (
            <button
              type="button"
              aria-label="Edit Social"
              title="Edit Social"
              onClick={onEdit}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--border)] text-[var(--primary)] transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30"
            >
              <Pencil size={16} aria-hidden="true" />
            </button>
          ) : (
            <div />
          )}
        </Card.Action>
      </Card.Header>

      <Card.Content id="SocialCard-CardContent" className="before:content-none after:content-none">
        <div className="flex flex-wrap gap-2">
          {ALL_PLATFORMS.filter((platform) => canEdit || linkMap[platform]).map((platform) => (
            <SocialIconButton
              key={platform}
              platform={platform}
              href={linkMap[platform]}
              disabled={!linkMap[platform]}
              ariaLabel={!linkMap[platform] ? `${platform} profile not set` : undefined}
            />
          ))}
        </div>
      </Card.Content>

      <Card.Footer id="SocialCard-CardFooter">
        <div />
      </Card.Footer>
      </Card>
    </div>
  );
}

export default BasicSocialLinksCard;
